import React, { createContext, useCallback, useContext, useEffect, useRef, ReactNode } from "react";
import {
  useSaveSearchAnalyticsMutation,
  useSaveNoResultSearchAnalyticsMutation,
} from "Services/SearchAnalyticsApiSlice";
import { decodeParamList, decodeParam } from "Utilities/urlEncoder";

interface AnalyticsEntry {
  searchType: string;
  searchData: string;
  searchDateTime: string;
  selectedProductId?: string | null;
}

interface SearchAnalyticsContextType {
  queueAnalytics: (searchType: string, searchData: string, selectedProductId?: string | null) => void;
  resolveAnalytics: (hasResults: boolean) => void;
  attachProductToLastSearch: (productId: string) => void;
  clearAnalyticsQueue: () => void;
}

const SEARCH_LOGS_KEY = "sa_search_logs";
const NO_RESULT_LOGS_KEY = "sa_no_result_logs";
const FLUSH_INTERVAL_MS = 3_600_000; // 1 hour
// const FLUSH_INTERVAL_MS = 60_000; // 1 min

const SearchAnalyticsContext = createContext<SearchAnalyticsContextType | undefined>(undefined);

const getDateTime = (): string => new Date().toISOString().slice(0, 23);

const readLogs = (key: string): AnalyticsEntry[] => {
  try {
    return JSON.parse(localStorage.getItem(key) || "[]");
  } catch {
    return [];
  }
};

const writeLogs = (key: string, logs: AnalyticsEntry[]): void => {
  localStorage.setItem(key, JSON.stringify(logs));
};

export const getAnalyticsContextFromUrl = (defaultData: string, productId: string) => {
  if (typeof window === "undefined") return { searchType: "BOOK", searchData: defaultData, selectedProductId: productId };
  
  const searchParams = new URLSearchParams(window.location.search);
  
  const urlToTypeMap: Record<string, string> = {
    search: "SEARCH_BAR",
    subCategory: "SUB_CATEGORY",
    category: "CATEGORY",
    author: "AUTHOR",
    merchandise: "MERCHANDISE",
    language: "LANGUAGE",
    academic: "ACADEMIC",
    bookCondition: "BOOK_CONDITION",
    fromprice: "PRICE",
  };

  const activeTypes = new Set<string>();
  for (const [key, type] of Object.entries(urlToTypeMap)) {
    if (searchParams.has(key)) {
      activeTypes.add(type);
    }
  }

  if (activeTypes.size > 0) {
    try {
      const logs = JSON.parse(localStorage.getItem("sa_search_logs") || "[]");
      for (let i = logs.length - 1; i >= 0; i--) {
        const log = logs[i];
        if (activeTypes.has(log.searchType) && log.searchData) {
          return {
            searchType: log.searchType,
            searchData: log.searchData,
            selectedProductId: productId
          };
        }
      }
    } catch {
      // ignore
    }

    const orderedKeys = [
      "search",
      "subCategory",
      "category",
      "author",
      "merchandise",
      "language",
      "academic",
      "bookCondition",
      "fromprice",
    ];

    for (const key of orderedKeys) {
      if (searchParams.has(key)) {
        let fallbackData = defaultData;
        if (key === "search") fallbackData = decodeURIComponent(searchParams.get(key) || defaultData);
        else if (key === "fromprice") {
           const from = decodeParam(searchParams.get("fromprice") || "");
           const to = searchParams.has("toprice") ? decodeParam(searchParams.get("toprice") || "") : "above";
           fallbackData = `${from}-${to}`;
        } else {
           fallbackData = decodeParamList(searchParams.get(key) || "")[0] || defaultData;
        }
        return {
          searchType: urlToTypeMap[key],
          searchData: fallbackData,
          selectedProductId: productId
        };
      }
    }
  }
  
  return { searchType: "BOOK", searchData: defaultData, selectedProductId: productId };
};

export const SearchAnalyticsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [saveSearchAnalytics] = useSaveSearchAnalyticsMutation();
  const [saveNoResultSearchAnalytics] = useSaveNoResultSearchAnalyticsMutation();

  const pendingRef = useRef<{ searchType: string; searchData: string; selectedProductId?: string | null }[]>([]);
  const flushRef = useRef<() => Promise<void>>();

  const flushAnalytics = useCallback(async () => {
    const searchLogs = readLogs(SEARCH_LOGS_KEY);
    const noResultLogs = readLogs(NO_RESULT_LOGS_KEY);

    if (searchLogs.length > 0) {
      try {
        await saveSearchAnalytics({
          origin: window.location.origin,
          body: { analyticsList: searchLogs },
        }).unwrap();
        writeLogs(SEARCH_LOGS_KEY, []);
      } catch {
        // keep data; will retry on next flush
      }
    }

    if (noResultLogs.length > 0) {
      try {
        await saveNoResultSearchAnalytics({
          origin: window.location.origin,
          body: { analyticsList: noResultLogs },
        }).unwrap();
        writeLogs(NO_RESULT_LOGS_KEY, []);
      } catch {
        // keep data; will retry on next flush
      }
    }
  }, [saveSearchAnalytics, saveNoResultSearchAnalytics]);

  useEffect(() => {
    flushRef.current = flushAnalytics;
  }, [flushAnalytics]);

  useEffect(() => {
    const interval = setInterval(() => {
      flushRef.current?.();
    }, FLUSH_INTERVAL_MS);

    // Flush immediately when connection is restored instead of waiting for the next tick
    const handleOnline = () => flushRef.current?.();
    window.addEventListener("online", handleOnline);

    return () => {
      clearInterval(interval);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  const queueAnalytics = useCallback((searchType: string, searchData: string, selectedProductId?: string | null) => {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      return;
    }
    const trimmed = searchData.trim();
    if (!trimmed) return;
    pendingRef.current.push({ searchType, searchData: trimmed, selectedProductId });
  }, []);

  const resolveAnalytics = useCallback((hasResults: boolean) => {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      pendingRef.current = [];
      return;
    }
    const pending = pendingRef.current.splice(0);
    if (pending.length === 0) return;

    const newLogs: AnalyticsEntry[] = pending.map((item) => ({
      searchType: item.searchType,
      searchData: item.searchData,
      searchDateTime: getDateTime(),
      ...(item.selectedProductId ? { selectedProductId: item.selectedProductId } : {}),
    }));

    if (hasResults) {
      writeLogs(SEARCH_LOGS_KEY, [...readLogs(SEARCH_LOGS_KEY), ...newLogs]);
    } else {
      writeLogs(NO_RESULT_LOGS_KEY, [...readLogs(NO_RESULT_LOGS_KEY), ...newLogs]);
    }
  }, []);

  const clearAnalyticsQueue = useCallback(() => {
    pendingRef.current = [];
  }, []);

  const attachProductToLastSearch = useCallback((productId: string) => {
    const context = getAnalyticsContextFromUrl("BOOK", productId);
    const searchLogs = readLogs(SEARCH_LOGS_KEY);
    
    let foundIndex = -1;
    for (let i = searchLogs.length - 1; i >= 0; i--) {
      if (searchLogs[i].searchType === context.searchType && searchLogs[i].searchData === context.searchData) {
        foundIndex = i;
        break;
      }
    }
    
    if (foundIndex !== -1) {
      if (!searchLogs[foundIndex].selectedProductId) {
        searchLogs[foundIndex].selectedProductId = productId;
        writeLogs(SEARCH_LOGS_KEY, searchLogs);
        return;
      }
    }
    
    queueAnalytics(context.searchType, context.searchData, productId);
    resolveAnalytics(true);
  }, [queueAnalytics, resolveAnalytics]);

  return (
    <SearchAnalyticsContext.Provider value={{ queueAnalytics, resolveAnalytics, attachProductToLastSearch, clearAnalyticsQueue }}>
      {children}
    </SearchAnalyticsContext.Provider>
  );
};

export const useSearchAnalytics = (): SearchAnalyticsContextType => {
  const context = useContext(SearchAnalyticsContext);
  if (!context) throw new Error("useSearchAnalytics must be used within SearchAnalyticsProvider");
  return context;
};
